import { useWebsocketTokenMap } from "../classes/WebsocketTokenMap.js";

/**
 * ==========================================
 * ReverbChannelPlugin / Echo wrapper (V2)
 * ==========================================
 *
 * Mire való?
 * Ez a plugin a bejelentkezett felhasználó privát Echo csatornájára iratkozik fel
 * Reverb alapú websocket kommunikációhoz (Echo.private(...)),
 * és a bejövő broadcast eventeket továbbadja a regisztrált callbackeknek.
 *
 * Fő feladatai:
 * - privát user channelre való feliratkozás
 * - bejövő realtime események fogadása
 * - callbackek regisztrálása / törlése
 * - feliratkozási állapot követése
 * - opcionális kliensoldali whisper üzenetküldés
 *
 * V1 → V2 váltás röviden:
 * - a korábbi V1 egyszerű objektumos callback tárolást használt
 * - a V1 az "App.Models.User.{id}" privát csatornára iratkozott fel
 * - a V2 Map + Symbol alapú callback kezelést használ
 * - a V2 külön kezeli a subscribed állapotot
 * - a V2 támogatja a subscribed utáni callbackeket is
 * - a V2 előkészített whisper / token kezelésre is
 *
 * Jelenlegi V2 működés:
 * - a plugin a getPrivateChannelKey() alapján privát csatornát képez
 * - a getPrivateChannel() ezen a csatornán Echo.private(...) kapcsolatot hoz létre
 * - startListening() feliratkozik a csatornára
 * - sikeres feliratkozáskor:
 *   - subscribed = true
 *   - lefutnak a registerSubscribedCallback() callbackek
 * - hiba esetén:
 *   - subscribed = false
 * - a listenToAll() minden bejövő eventet az eventHandler() felé továbbít
 * - az eventHandler() a regisztrált callbackek közül csak azokat futtatja,
 *   amelyek event neve egyezik, vagy "*" típusúak
 *
 * Callback kezelés:
 * - registerCallback(type, callback)
 *   → normál eseményfigyelő callback regisztrálása
 * - removeCallback(symbol)
 *   → normál callback törlése
 * - registerSubscribedCallback(callback)
 *   → olyan callback, ami a sikeres csatorna-feliratkozás után fusson le
 * - removeSubscribedCallback(symbol)
 *   → subscribed callback törlése
 *
 * Privát csatorna kulcs:
 * - jelenleg UUID alapú: "User.{uuid}"
 * - FONTOS: ennek egyeznie kell a backend Broadcast::channel definícióval
 * - ha a backend még a régi mintát használja, akkor a channel névhez is igazodni kell
 *
 * WebsocketTokenMap:
 * - Singleton in-memory tároló (channel -> token)
 * - az Echo authorizer (/broadcasting/auth) válaszából kapott token tárolható benne
 * - a whisper payload-ba opcionálisan bekerülhet
 *
 * postMessage() / whisper:
 * - kliens oldali üzenetküldés a privát csatornára
 * - az Echo a type-ot automatikusan "client-" prefixszel küldi
 *
 * Megjegyzés:
 * - a plugin alap része a private channel feliratkozás és a broadcast eventek fogadása
 * - a WebsocketTokenMap és a postMessage() / whisper rész egy külön,
 *   opcionális kliensoldali websocket bővítéshez tartozik
 * - ha a projekt csak backend → frontend realtime eseményeket használ,
 *   akkor ez az extra tokenes réteg nem feltétlenül szükséges
 */

export default class {
    constructor() {
        this.callbacks = new Map();
        this.callbacksSubscribed = new Map();
        this.WebsocketTokenMap = useWebsocketTokenMap();
        this.subscribed = null;
        this.channel = null;
    }

    install(app, option) {
        this.app = app;

        this.startListening();

        const exposed = {
            registerCallback: this.registerCallback.bind(this),
            removeCallback: this.removeCallback.bind(this),
            registerSubscribedCallback: this.registerSubscribedCallback.bind(this),
            removeSubscribedCallback: this.removeSubscribedCallback.bind(this),
            postMessage: this.postMessage.bind(this),
            isSubscribed: this.isSubscribed.bind(this),
            getPrivateChannelKey: this.getPrivateChannelKey.bind(this),
        };

        app.config.globalProperties.$reverb = exposed;
        app.provide('Reverb', exposed);
    }
    /**
     * Privát csatorna kulcs:
     * - UUID alapú: "User.{uuid}"
     * - a korábbi V1 implementáció ID alapú volt: "App.Models.User.{id}"
     * - a V2 teljes mértékben UUID alapú működésre lett átállítva
     *
     * FONTOS:
     * - ennek pontosan egyeznie kell a backend Broadcast::channel definícióval
     * - frontend: Echo.private('User.' + user.uuid)
     * - backend:  Broadcast::channel('User.{uuid}', ...)
     *
     * Ha nem egyeznek → a websocket kapcsolat nem jön létre
     */
    getPrivateChannelKey() {
        return 'User.' + this.app.config.globalProperties.$user.data.uuid;
    }

    getPrivateChannel() {
        if (!this.channel) {
            this.channel = Echo.private(this.getPrivateChannelKey());
        }

        return this.channel;
    }

    isSubscribed() {
        return this.subscribed;
    }

    startListening() {
        if (!this.app.config.globalProperties.$user.isLoggedIn()) {
            return;
        }

        this.getPrivateChannel()
            .subscribed(() => {
                this.subscribed = true;

                this.callbacksSubscribed.forEach((callback, symbol) => {
                    try {
                        callback();
                    } finally {
                        this.removeSubscribedCallback(symbol);
                    }
                });
            })
            .error((error) => {
                this.subscribed = false;
                console.error('Error when connecting to the private channel');
                console.log(error);
            })
            .listenToAll(this.eventHandler.bind(this));
    }

    eventHandler(name, data) {
        this.callbacks.forEach((entry) => {
            if (entry.type === name || entry.type === '*') {
                entry.callback(data, name);
            }
        });
    }

    registerSubscribedCallback(callback) {
        if (this.subscribed === true) {
            callback();
            return null;
        }

        const symbol = Symbol();
        this.callbacksSubscribed.set(symbol, callback);

        return symbol;
    }

    removeSubscribedCallback(symbol) {
        this.callbacksSubscribed.delete(symbol);
        return this;
    }

    registerCallback(type, callback) {
        const symbol = Symbol();

        this.callbacks.set(symbol, {
            type,
            callback,
        });

        return symbol;
    }

    removeCallback(symbol) {
        this.callbacks.delete(symbol);
        return this;
    }

    /**
     * Kliens oldali whisper küldés.
     * Az Echo automatikusan "client-" prefixet tesz az event névre.
     */
    postMessage(type, data) {
        this.getPrivateChannel().whisper(type, {
            token: this.WebsocketTokenMap.get('private-' + this.getPrivateChannelKey()) ?? '',
            user_uuid: this.app.config.globalProperties.$user.data.uuid,
            data,
        });
    }
}